import { useMemo } from "react";
import { format } from "date-fns";
import { Button, DatePicker, Select, Switch, TextField, Typography } from "theme";
import { CrossMedium } from "theme/icons";

import { Autocomplete, Box, FormControl, FormControlLabel,InputLabel, MenuItem, useTheme } from "@mui/material";
import { styled } from "@mui/system";

import { useBookings } from "../../../api/hooks/useBookings/useBookings.ts";
import { useClients } from "../../../api/hooks/useClients/useClients.tsx";
import useHalls from "../../../api/hooks/useHalls/useHalls.ts";
import DatepickerPopper from "../../../theme/components/DatePicker/DatepickerPopper.tsx";
import { EFilteringState } from "../../../utils/enums/EFilteringState.ts";
import { EOrder } from "../../../utils/enums/ESortingOrder.ts";
import { useFilterSync } from "../../../utils/hooks/useFiltersSync.ts";

import Table from "./Table.tsx";

const StyledFiltersWrapper = styled(Box)(({ theme }) => ({
  borderBottomWidth: 1,
  borderBottomStyle: "solid",
  borderBottomColor: theme.palette.secondary.contrastText,
  display: "flex",
  alignItems: "center",
  columnGap: theme.spacing(5),
  padding: theme.spacing(1.5, 2.5),
}));

const BookingTable = () => {
  const { halls = [] } = useHalls();
  const { palette } = useTheme();

  const initialState = useMemo(() => ({
    quickSearch: "",
    clientId: null,
    hallId: "",
    startDate: null,
    endDate: null,
    page: 0,
    pageSize: 20,
    sendDeleted: false,
    order: EOrder.ASC,
    filtering: EFilteringState.StartDate
  }), []);

  const { updateFilters, filters, clearFilters } = useFilterSync({
    storageKey: "bookingsList",
    initialState,
  });

  const { data, isFetching } = useBookings({
    startDate: filters.startDate ? format(new Date(filters.startDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'") : null,
    endDate: filters.endDate ? format(new Date(filters.endDate), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'") : null,
    hallId: filters.hallId || null,
    page: filters.page + 1,
    pageSize: filters.pageSize,
    // @ts-ignore
    clientId: filters.clientId?.id || null,
    sendDeleted: filters.sendDeleted,
    order: filters.order,
    filtering: filters.filtering
  });

  const { data: clients, isFetching: isClientsFetching } = useClients({
    page: 1,
    pageSize: 10,
    quickSearch: filters.quickSearch,
  });

  return (
    <Box>
      <StyledFiltersWrapper>
        <Autocomplete fullWidth options={clients?.clients || []}
                      value={filters.clientId}
                      onChange={(_, newValue) => {
                        updateFilters({
                          clientId: newValue as null,
                          page: 0
                        });
                      }}
                      getOptionLabel={(option) => `${option.name} ${option.phone}`}
                      renderOption={(props, option) => {
                        return <Box component='li' {...props} key={option.id}>
                          <div>{option.name} <br /> {option.phone}</div>
                        </Box>
                      }}
                      loading={isClientsFetching}
                      loadingText="Loading..."
                      inputValue={filters.quickSearch}
                      onInputChange={(_, newValue) => updateFilters({
                        quickSearch: newValue,
                      })}
                      renderInput={params => <TextField name="search" {...params} label="Пошук" />} />
        <FormControl fullWidth>
          <InputLabel id="hall" style={{
            color: palette.text.primary,
          }}>Локація</InputLabel>
          <Select
            fullWidth
            MenuProps={{
              sx: {
                "& .MuiPaper-root": {
                  background: palette.primary.main,
                },
              },
            }}
            value={filters.hallId}
            labelId="hall"
            onChange={(e) => updateFilters({
              hallId: e.target.value as string,
              page: 0
            })}
          >
            {halls.map((el) => {
              return (
                <MenuItem key={el.id} value={el.id}>
                  {el.name}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <DatePicker slots={{
          popper: DatepickerPopper,
        }} label="Початкова дата"
                    value={typeof filters.startDate === "string" ? new Date(filters.startDate) : filters.startDate}
                    onChange={value => updateFilters({
                      startDate: value as null,
                      page: 0
                    })} />
        -
        <DatePicker slots={{
          popper: DatepickerPopper,
        }} label="Кінцева дата"
                    value={typeof filters.endDate === "string" ? new Date(filters.endDate) : filters.endDate}
                    onChange={value => updateFilters({
                      endDate: value as null,
                      page: 0
                    })} />
      </StyledFiltersWrapper>

      <StyledFiltersWrapper>
        <FormControl fullWidth>
          <InputLabel id="hall" style={{
            color: palette.text.primary,
          }}>Сортування</InputLabel>
          <Select
            fullWidth
            MenuProps={{
              sx: {
                "& .MuiPaper-root": {
                  background: palette.primary.main,
                },
              },
            }}
            value={filters.order}
            labelId="order"
            onChange={(e) => updateFilters({
              order: e.target.value as EOrder,
              page: 0
            })}
          >
            {Object.values(EOrder).map((el) => {
              return (
                <MenuItem key={el} value={el}>
                  {el === EOrder.ASC ? "По зростанню" : "По спаданню"}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>


        <FormControl fullWidth>
          <InputLabel id="hall" style={{
            color: palette.text.primary,
          }}>Фільтрація</InputLabel>
          <Select
            fullWidth
            MenuProps={{
              sx: {
                "& .MuiPaper-root": {
                  background: palette.primary.main,
                },
              },
            }}
            value={filters.filtering}
            labelId="filtering"
            onChange={(e) => updateFilters({
              filtering: e.target.value as EFilteringState,
              page: 0
            })}
          >
            {Object.values(EFilteringState).map((el) => {
              return (
                <MenuItem key={el} value={el}>
                  {el === EFilteringState.StartDate ? "Дата бронювання" : "Дата створення"}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={filters.sendDeleted}
                sx={{ m: 1 }}
                onChange={() => updateFilters({
                  sendDeleted: !filters.sendDeleted,
                })}
              />
            }
            label={
              <Typography variant="body1" weight="semiBold">
                Показати видалені
              </Typography>
            }
          />
        </Box>
        <Button onClick={clearFilters} title="Очистити" look="normal">
          <CrossMedium />
        </Button>
      </StyledFiltersWrapper>

      <Box p={2.5}>
        <Table data={data} loading={isFetching} paginationModel={{
          page: filters.page,
          pageSize: filters.pageSize,
        }} setPaginationModel={value => {
          updateFilters({
            page: value.pageSize === filters.pageSize ? value.page : 0,
            pageSize: value.pageSize,
          });
        }} />
      </Box>
    </Box>
  );
};

export default BookingTable;
